import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button, EmptyState, ErrorState, Skeleton } from '../../components';
import { useLoadState } from '../../lib/useLoadState';

import { useDocumentsApi } from './client';
import './documents.css';

type DownloadPhase = 'idle' | 'downloading' | 'done' | 'error';

/**
 * Where the export-ready email's link lands. Polls the job's status once, then
 * — the moment it's `DONE` — performs the same authenticated fetch-blob-download
 * recipe `DocumentDetailScreen`'s own "Download original" uses (the bearer token
 * can't ride a bare `<a href>`, which is exactly why the email links here
 * instead of straight at a downloadable URL). `PENDING`/`PROCESSING` and
 * `FAILED` get their own message; a 404 (expired/foreign/malformed id) never
 * distinguishes itself from any other fetch failure — this is not a screen a
 * caller can use to probe which export ids exist.
 */
export function ExportDownloadScreen(): React.JSX.Element {
  const { exportId = '' } = useParams<{ exportId: string }>();
  const docsApi = useDocumentsApi();

  const fetchStatus = useCallback(
    (signal: AbortSignal) => docsApi.exportStatus(exportId, signal),
    [docsApi, exportId],
  );
  const state = useLoadState(fetchStatus);

  const [phase, setPhase] = useState<DownloadPhase>('idle');

  const download = useCallback(() => {
    setPhase('downloading');
    void (async () => {
      try {
        const blob = await docsApi.exportDownload(exportId);
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = `documents-export-${exportId}.csv`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Revoked on the next tick, not immediately — some browsers read the
        // blob asynchronously after the click and a same-tick revoke cancels it.
        window.setTimeout(() => { URL.revokeObjectURL(url); }, 0);
        setPhase('done');
      } catch {
        setPhase('error');
      }
    })();
  }, [docsApi, exportId]);

  useEffect(() => {
    if (state.status === 'ready' && state.data.status === 'DONE' && phase === 'idle') {
      download();
    }
  }, [state, phase, download]);

  if (state.status === 'loading') {
    return (
      <section className="ds-docd" aria-label="Export download">
        <Skeleton height={160} />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="ds-docd" aria-label="Export download">
        <ErrorState
          title="This export isn't available"
          body="The link may have expired, or the export may not exist."
          traceId={state.traceId ?? undefined}
          actions={
            <Link to="/documents">
              <Button variant="secondary">Back to Documents</Button>
            </Link>
          }
        />
      </section>
    );
  }

  const job = state.data;

  if (job.status === 'FAILED') {
    return (
      <section className="ds-docd" aria-label="Export download">
        <ErrorState
          title="This export failed"
          body="Something went wrong while building it. Start a new export from Documents."
          actions={
            <Link to="/documents">
              <Button variant="secondary">Back to Documents</Button>
            </Link>
          }
        />
      </section>
    );
  }

  if (job.status === 'PENDING' || job.status === 'PROCESSING') {
    return (
      <section className="ds-docd" aria-label="Export download">
        <EmptyState
          title="Still being prepared"
          body="This export is still being built — check back shortly."
          actions={
            <Button variant="primary" onClick={state.reload}>
              Check again
            </Button>
          }
        />
      </section>
    );
  }

  return (
    <section className="ds-docd" aria-label="Export download">
      <EmptyState
        title="Your export"
        body={
          phase === 'error'
            ? 'The download could not start automatically.'
            : `${String(job.row_count ?? 0)} row(s) — your download should have started.`
        }
        actions={
          <Button variant="primary" onClick={download} loading={phase === 'downloading'}>
            Download CSV
          </Button>
        }
      />
    </section>
  );
}
